import {useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {ZoomControls} from '@/components/perfil/organigrama-diagrama-controls'
import {computeTreeLayout, findRoots, BOX_WIDTH, BOX_HEIGHT, type ReporteRow} from '@/lib/organigrama-layout'
import {cn} from '@/lib/utils'

const PADDING = 24

/** HTML, not SVG, for real text layout; positioned over the SVG connector layer. */
function PersonaBox({row}: {row: ReporteRow}) {
  const {t} = useTranslation()
  return (
    <div
      className="surface flex h-full w-full flex-col justify-center gap-0.5 overflow-hidden px-2.5 py-1.5 text-center"
      data-testid={`organigrama-diagrama-caja-${row.employee_id}`}>
      <p className="truncate text-xs font-semibold text-foreground">{row.nombre_completo}</p>
      {/* truncate, not Tag's break-words: the box has a fixed height and overflow-hidden. */}
      {row.cargo && (
        <Tag tone="neutral" className="mx-auto block max-w-full truncate">
          {row.cargo}
        </Tag>
      )}
      {row.area && <p className="truncate text-[10px] text-muted-foreground">{row.area}</p>}
      {!row.cargo && !row.area && (
        <p className="text-[10px] text-muted-foreground">{t('business.organigrama.sinCargo')}</p>
      )}
    </div>
  )
}

/** Reads the live CSS custom properties: the design-token gate forbids literal hex. */
function resolveExportPalette() {
  const styles = getComputedStyle(document.documentElement)
  const read = (name: string, fallback: string) => styles.getPropertyValue(name).trim() || fallback
  return {
    line: read('--chart-verificar', 'currentColor'),
    boxFill: read('--card', 'white'),
    boxStroke: read('--border', 'currentColor'),
    name: read('--foreground', 'currentColor'),
    meta: read('--muted-foreground', 'currentColor'),
  }
}

/** Pure SVG for export: cheaper and more reliable than rasterizing the live HTML. */
function buildExportSvg(rows: ReporteRow[]): string {
  const layout = computeTreeLayout(rows)
  const w = layout.width + PADDING * 2
  const h = layout.height + PADDING * 2
  const byId = new Map(layout.nodes.map((n) => [n.employee_id, n]))
  const palette = resolveExportPalette()

  const edgeLines = layout.edges
    .map((edge) => {
      const from = byId.get(edge.from)
      const to = byId.get(edge.to)
      if (!from || !to) return ''
      const x1 = from.x + PADDING + BOX_WIDTH / 2
      const y1 = from.y + PADDING + BOX_HEIGHT
      const x2 = to.x + PADDING + BOX_WIDTH / 2
      const y2 = to.y + PADDING
      const midY = (y1 + y2) / 2
      return `<path d="M ${x1} ${y1} V ${midY} H ${x2} V ${y2}" fill="none" stroke="${palette.line}" stroke-width="1.5" />`
    })
    .join('')

  const boxes = layout.nodes
    .map((node) => {
      const x = node.x + PADDING
      const y = node.y + PADDING
      const name = escapeXml(node.row.nombre_completo)
      const meta = escapeXml([node.row.cargo, node.row.area].filter(Boolean).join(' · '))
      return `
        <rect x="${x}" y="${y}" width="${BOX_WIDTH}" height="${BOX_HEIGHT}" rx="8" fill="${palette.boxFill}" stroke="${palette.boxStroke}" stroke-width="1" />
        <text x="${x + BOX_WIDTH / 2}" y="${y + 22}" text-anchor="middle" font-size="11" font-weight="600" font-family="sans-serif" fill="${palette.name}">${name}</text>
        <text x="${x + BOX_WIDTH / 2}" y="${y + 38}" text-anchor="middle" font-size="9" font-family="sans-serif" fill="${palette.meta}">${meta}</text>
      `
    })
    .join('')

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"><rect width="${w}" height="${h}" fill="${palette.boxFill}" />${edgeLines}${boxes}</svg>`
}

function escapeXml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;'})[c]!)
}

/** `data:` URIs because the CSP (`infra/firebase/firebase.json`) allows `img-src data:` but not
 * `blob:`. The link is attached before the click so Firefox honours `download`. */
function downloadSvgAsPng(svgMarkup: string, filename: string, width: number, height: number) {
  const img = new Image()
  img.onload = () => {
    const scale = 2
    const canvas = document.createElement('canvas')
    canvas.width = width * scale
    canvas.height = height * scale
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.scale(scale, scale)
    ctx.drawImage(img, 0, 0)
    const link = document.createElement('a')
    link.href = canvas.toDataURL('image/png')
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
  }
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`
}

export function OrganigramaDiagrama({reporte}: {reporte: ReporteRow[]}) {
  const {t} = useTranslation()
  const [zoom, setZoom] = useState<number>(1)
  const [isPanning, setIsPanning] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const panStart = useRef<{x: number; y: number; scrollLeft: number; scrollTop: number} | null>(null)

  const layout = useMemo(() => computeTreeLayout(reporte), [reporte])
  const byId = useMemo(() => new Map(layout.nodes.map((n) => [n.employee_id, n])), [layout])

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    const el = containerRef.current
    // Mouse only: touch and pen keep the container's native scroll and pinch.
    if (!el || e.pointerType !== 'mouse') return
    panStart.current = {x: e.clientX, y: e.clientY, scrollLeft: el.scrollLeft, scrollTop: el.scrollTop}
    setIsPanning(true)
    el.setPointerCapture(e.pointerId)
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const el = containerRef.current
    const start = panStart.current
    if (!el || !start) return
    el.scrollLeft = start.scrollLeft - (e.clientX - start.x)
    el.scrollTop = start.scrollTop - (e.clientY - start.y)
  }

  function endPan(e: React.PointerEvent<HTMLDivElement>) {
    const el = containerRef.current
    if (el && panStart.current) el.releasePointerCapture(e.pointerId)
    panStart.current = null
    setIsPanning(false)
  }

  if (reporte.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">{t('business.organigrama.empty')}</p>
  }

  // `build_reporte` guarantees a root, but a diagram with zero boxes would be blank: flat fallback.
  if (findRoots(reporte).length === 0) {
    return (
      <div className="space-y-3" data-testid="organigrama-diagrama">
        <p className="text-sm text-tone-amber-fg">{t('business.organigrama.cicloDetectado')}</p>
        <div className="flex flex-wrap gap-3" data-testid="organigrama-diagrama-plana">
          {reporte.map((row) => (
            <div key={row.employee_id} style={{width: BOX_WIDTH, height: BOX_HEIGHT}}>
              <PersonaBox row={row} />
            </div>
          ))}
        </div>
      </div>
    )
  }

  const width = layout.width + PADDING * 2
  const height = layout.height + PADDING * 2

  function handleExportPng() {
    const svgMarkup = buildExportSvg(reporte)
    downloadSvgAsPng(svgMarkup, 'organigrama.png', width, height)
  }

  return (
    <div className="space-y-3" data-testid="organigrama-diagrama">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <ZoomControls zoom={zoom} onZoomChange={setZoom} />
          <span className="text-[11px] text-muted-foreground">{t('business.organigrama.diagrama.panHint')}</span>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleExportPng}
          data-testid="organigrama-diagrama-export-png">
          {t('business.organigrama.diagrama.exportPng')}
        </Button>
      </div>
      <div
        ref={containerRef}
        data-testid="organigrama-diagrama-lienzo"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endPan}
        onPointerCancel={endPan}
        className={cn(
          'max-h-[70vh] overflow-auto rounded-lg border border-border bg-secondary/40 p-4',
          isPanning ? 'cursor-grabbing select-none' : 'cursor-grab'
        )}>
        <div style={{width: width * zoom, height: height * zoom}}>
          <div style={{width, height, transform: `scale(${zoom})`, transformOrigin: 'top left'}} className="relative">
            <svg width={width} height={height} className="absolute top-0 left-0" aria-hidden="true">
              {layout.edges.map((edge) => {
                const from = byId.get(edge.from)
                const to = byId.get(edge.to)
                if (!from || !to) return null
                const x1 = from.x + PADDING + BOX_WIDTH / 2
                const y1 = from.y + PADDING + BOX_HEIGHT
                const x2 = to.x + PADDING + BOX_WIDTH / 2
                const y2 = to.y + PADDING
                const midY = (y1 + y2) / 2
                return (
                  <path
                    key={`${edge.from}-${edge.to}`}
                    d={`M ${x1} ${y1} V ${midY} H ${x2} V ${y2}`}
                    fill="none"
                    stroke="var(--chart-verificar)"
                    strokeWidth={1.5}
                  />
                )
              })}
            </svg>
            {layout.nodes.map((node) => (
              <div
                key={node.employee_id}
                className="absolute"
                style={{left: node.x + PADDING, top: node.y + PADDING, width: BOX_WIDTH, height: BOX_HEIGHT}}>
                <PersonaBox row={node.row} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
